import React, { useEffect, useState } from 'react';
import { CertificatePage } from '../../modules/tt-tokenized-assets/index';
import { useParams } from 'react-router-dom';
import { useNavigate } from 'react-router';
import { openNotification } from 'tt-ui-lib/core';
import { useLazyQuery, useMutation } from '@apollo/client';
import {
  CARBON_CREATE_REGISTER,
  CARBON_GET_DATA,
  CARBON_GET_OFFSET_DATA,
  CARBON_GET_REGISTERS,
  CARBON_OFFSET,
  CARBON_SNAP_TO_ADDRESS,
} from '../../api';
import { useSelectedContext } from '../../context/contextSelector';
import { useDigitalAssets } from 'modules/tt-digital-assets-provider';
import moment from 'moment';

const CarbonCertificatePage = () => {
  const navigate = useNavigate();
  const { id } = useParams();
  const { account, connect, status } = useDigitalAssets();

  const [createRegisterRequest] = useMutation(CARBON_CREATE_REGISTER);
  const [getCertificateRequest] = useMutation(CARBON_OFFSET);
  const [certificateMoveToWalletRequest] = useMutation(CARBON_SNAP_TO_ADDRESS);
  const [getCarbonOffsetDataRequest] = useMutation(CARBON_GET_OFFSET_DATA);
  // const [certificateMoveToWallet] = useMutation(CARBON_MOVE_TO_WALLET);

  const [getCarbonDataRequest] = useLazyQuery(CARBON_GET_DATA, {
    fetchPolicy: 'network-only',
  });
  const [getCarbonRegistersRequest] = useLazyQuery(CARBON_GET_REGISTERS, {
    fetchPolicy: 'network-only',
  });

  const { user, selectedCompany } = useSelectedContext((context) => context.user);

  const [data, setData] = useState(null);
  const [offsetData, setOffsetData] = useState([]);
  const [certLoading, setCertLoading] = useState(false);
  const [updateCertImage, setUpdateCertImage] = useState(true);
  const [updateCertData, setUpdateCertData] = useState(true);
  const [isLoading, setIsLoading] = useState(false);
  const [isMoveLoading, setIsMoveLoading] = useState(false);

  const selectedCompanyId = selectedCompany?.isCompanySelected
    ? selectedCompany?.selectedCompany?.id || undefined
    : undefined;

  const getCertificateData = () => {
    setIsLoading(true);

    getCarbonDataRequest({ variables: { id } })
      .then((response) => {
        if (!response?.data?.getUserCarbonCredit) {
          openNotification({
            type: 'error',
            message: 'Something went wrong, please try again',
          });
          return;
        }

        setData(response?.data?.getUserCarbonCredit || null);
      })
      .catch(() => {
        openNotification({
          type: 'error',
          message: 'Something went wrong, please try again',
        });
      })
      .finally(() => {
        setIsLoading(false);
      });
  };

  const getOffsetDataCertificate = (payload, onSuccess, onError, onFinally) => {
    getCarbonOffsetDataRequest(payload)
      .then((response) => {
        if (onSuccess) {
          onSuccess(response);
        }
      })
      .catch((err) => {
        if (onError) {
          onError(err);
        }
      })
      .finally(() => {
        if (onFinally) {
          onFinally();
        }
      });
  };

  const getCertificate = (payload, onSuccess, onError) => {
    const input = {
      ...payload,
      company_id: selectedCompanyId,
    };

    getCertificateRequest(input)
      .then((response) => {
        if (onSuccess) {
          onSuccess(response);
        }
      })
      .catch((err) => {
        if (onError) {
          onError(err);
        }
      });
  };

  const getCertificateRegisters = (payload, onSuccess, onError) => {
    getCarbonRegistersRequest({
      variables: selectedCompanyId
        ? { ...payload, company_id: selectedCompanyId }
        : { ...payload, user_id: user.id },
    })
      .then((response) => {
        if (onSuccess) {
          onSuccess(response);
        }
      })
      .catch((err) => {
        if (onError) {
          onError(err);
        }
      });
  };

  const createRegister = (payload, onSuccess, onError) => {
    const input = {
      user_id: user.id,
      name: payload.name,
      description: payload.description,
      unit: payload.unit,
      carbon_ucr_credit_id: id,
      date: moment(payload.date).format('YYYY-MM-DD'),
      company_id: selectedCompany?.isCompanySelected
        ? selectedCompany?.selectedCompany?.id || undefined
        : undefined,
    };

    createRegisterRequest({ variables: { input } })
      .then((response) => {
        navigate(
          `/dashboard/decarbonizator/certificate/${id}/register/${response.data.createRegistry.id}`
        );

        if (onSuccess) {
          onSuccess();
        }
      })
      .catch((err) => {
        openNotification({
          type: 'error',
          message: 'Something went wrong, please try again',
        });

        if (onError) {
          onError(err);
        }
      });
  };

  const connectWalletHandler = () => {
    setIsMoveLoading(true);
    connect()
      .then(() => {
        openNotification({
          type: 'success',
          message: 'Wallet connected, now you can proceed moving certificate.',
        });
      })
      .finally(() => {
        setIsMoveLoading(false);
      });
  };

  const moveToWallet = () => {
    setIsMoveLoading(true);
    // TODO
    const input = {
      carbon_ucr_credit_id: id,
      uids: [id],
      accounts: [account],
    };

    certificateMoveToWalletRequest({ variables: { input } })
      .then(() => {
        window.location.replace(
          `${process.env.REACT_APP_DIGITAL_ASSETS}/dashboard/certificate/${id}`
        );
      })
      .catch((err) => {
        console.log(err);
        openNotification({
          type: 'error',
          message: 'Something went wrong, please try again later.',
        });
      })
      .finally(() => {
        setIsMoveLoading(false);
      });
  };

  useEffect(() => {
    if (id && updateCertData) {
      getCertificateData();
      setUpdateCertData(false);
    }
  }, [updateCertData]);

  useEffect(() => {
    if (updateCertImage) {
      setCertLoading(true);
      getOffsetDataCertificate(
        { variables: { input: { carbon_ucr_credit_id: id } } },
        (response) => {
          if (response && response?.data?.getAllOffsetData[0]) {
            setOffsetData(response.data.getAllOffsetData);
            setCertLoading(false);
          }
        },
        () => {
          setCertLoading(false);
        },
        () => {
          setCertLoading(false);
        }
      );
      setUpdateCertImage(false);
    }
  }, [updateCertImage]);

  return (
    <>
      <CertificatePage
        account={account}
        data={{ ...data, offset_balance: data?.offset }}
        isLoading={isLoading}
        navigate={navigate}
        getCertificate={getCertificate}
        getOffsetDataCertificate={getOffsetDataCertificate}
        createRegister={createRegister}
        getCertificateRegisters={getCertificateRegisters}
        moveToWallet={moveToWallet}
        connectWalletHandler={connectWalletHandler}
        mode="fin"
        urlDA={process.env.REACT_APP_DIGITAL_ASSETS}
        backUrl="/dashboard/decarbonizator"
        offsetData={offsetData}
        setUpdateCertImage={setUpdateCertImage}
        setUpdateCertData={setUpdateCertData}
        certLoading={certLoading}
        walletStatus={status}
        isMoveLoading={isMoveLoading}
      />
    </>
  );
};

export default CarbonCertificatePage;
